<template>
    <div v-if="!question.complete && disabled && empty" class="GlsFormQuestion">
        <header class="QuestionHeader">
            <h3>{{ title }}</h3>
            <div class="QuestionHeader__subtitle">
                {{ question.metadata.emptyInformation }}
            </div>
            <!-- eslint-disable-next-line vue/no-v-html -->
            <p v-html="question.metadata.description" />
            <slot name="sendEmails" />
        </header>
    </div>
    <div v-else-if="!question.complete" class="GlsFormQuestion">
        <header class="QuestionHeader">
            <h3>{{ title }}</h3>
            <div class="QuestionHeader__subtitle">
                {{ disabled ? question.metadata.completedInformation : question.metadata.instructions }}
            </div>
            <!-- eslint-disable-next-line vue/no-v-html -->
            <p v-html="question.metadata.description" />
            <slot name="sendEmails" />
        </header>
        <EmailList
            v-if="(!disabled || !empty) && !singleEmail"
            :disabled="disabled"
            :model-value="modelValue"
            @update:model-value="onInput"
        />
        <GlsSingleEmailQuestion
            v-if="(!disabled || !empty) && singleEmail"
            :value="modelValue[0] || ''"
            @input="onInput"
        />
    </div>
    <div v-else-if="empty && (disabled || !allowAdd)" class="GlsFormQuestion">
        <header class="QuestionHeader">
            <h3>{{ title }}</h3>
            <div class="QuestionHeader__subtitle">
                {{ question.metadata.emptyInformation }}
            </div>
            <slot name="sendEmails" />
        </header>
    </div>
    <div v-else class="GlsFormQuestion">
        <header class="QuestionHeader">
            <h3>{{ title }}</h3>
            <div class="QuestionHeader__subtitle">
                {{ empty ? question.metadata.emptyInformation : question.metadata.completedInformation }}
            </div>
            <slot name="sendEmails" />
        </header>
        <EntriesTable
            :value="modelValue"
            :is-disabled="isEmailDisabled"
            :attr-type="attrType"
            :allow-add="allowAdd && !singleEmail"
            :single-email="singleEmail"
            @delete="deleteEmail"
            @add="addDataSubject"
            @resend="sendEmailLink"
        />
    </div>
</template>

<script lang="ts">
    import { Component, Prop, Vue } from 'vue-property-decorator';

    import Question from '@evidentid/ido-lib/interfaces/Question';

    import AddressInput from '@/components/forms/AddressInput.vue';
    import EmailList from '@/components/forms/EmailList.vue';
    import EntriesTable from '@/components/google/EntriesTable.vue';
    import GlsSingleEmailQuestion from '@/components/google/GlsSingleEmailQuestion/GlsSingleEmailQuestion.vue';

    // PRODUCT-26110 Task 12 (COMPONENT_V_MODEL): migrated to the Vue 3 v-model
    // contract. Consumed via v-model by google/GlsForm.vue (x3 instances).
    @Component({
        emits: [ 'add', 'delete', 'resend', 'update:modelValue' ],
        components: { EntriesTable, EmailList, AddressInput, GlsSingleEmailQuestion },
    })
    export default class GlsEmailQuestion extends Vue {
        @Prop({ type: Object })
        private question!: Question;

        @Prop({ type: Array, default: () => [] })
        private modelValue!: string[];

        @Prop({ type: Boolean, default: false })
        private disabled!: boolean;

        @Prop({ type: Function, default: () => true })
        private canDelete!: (email: string, index: number) => boolean;

        @Prop({ type: Boolean, default: true })
        private allowAdd!: boolean;

        @Prop({ type: Boolean, default: false })
        private singleEmail!: boolean;

        private get empty(): boolean {
            return Boolean(!this.modelValue || this.modelValue.length === 0);
        }

        private get title(): string {
            const { title, submittedTitle } = this.question.metadata;
            return this.disabled || this.question.complete ? submittedTitle || title : title;
        }

        private deleteEmail(email: string) {
            if (!this.disabled) {
                this.$emit('delete', email);
            }
        }

        private get attrType(): string {
            return this.question.attrType;
        }

        private isEmailDisabled(email: string, index: number): boolean {
            return this.disabled || !this.canDelete(email, index);
        }

        private sendEmailLink(email: string): void {
            if (!this.disabled) {
                this.$emit('resend', email);
            }
        }

        private addDataSubject(email: string): void {
            if (!this.disabled) {
                this.$emit('add', email);
            }
        }

        private onInput(email: string[]): void {
            if (!this.disabled) {
                this.$emit('update:modelValue', email);
            }
        }
    }
</script>
